// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canAddAllToList,
    canCheckoutWithCart,
    canSubmitForQuote,
    getCurrentCartState,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    isCartEmpty,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addLinesToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddLinesToCart";
import cancelApproval from "@insite/client-framework/Store/Pages/Cart/Handlers/CancelApproval";
import clearCurrentCart from "@insite/client-framework/Store/Pages/Cart/Handlers/ClearCurrentCart";
import discardEdit from "@insite/client-framework/Store/Pages/Cart/Handlers/DiscardEdit";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import saveOrder from "@insite/client-framework/Store/Pages/Cart/Handlers/SaveOrder";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, CartLineModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { HiddenProps } from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SavedOrderNameModal from "@steiner/App/Components/SavedOrder/SavedOrderNameModal";
import trackBeginCheckout from "@steiner/App/GoogleAnalyticsConnector/Handlers/Overrides/CheckoutShipping/TrackBeginCheckout";
import { xIsCartCheckoutDisabled } from "@steiner/App/Store/Selectors/CartsSelector";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    fullWidthButton = "fullWidthButton",
    checkoutVariant = "checkoutVariant",
    requestForQuoteVariant = "requestForQuoteVariant",
    saveOrderVariant = "saveOrderVariant",
    addAllToListVariant = "addAllToListVariant",
    // XNG-Changes
    isMiniCart = "isMiniCart",
}

const enum variants {
    primary = "primary",
    secondary = "secondary",
    overflow = "overflow",
    unenabled = "unenabled",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.fullWidthButton]: boolean;
        [fields.checkoutVariant]: variants;
        [fields.requestForQuoteVariant]: variants;
        [fields.saveOrderVariant]: variants;
        [fields.addAllToListVariant]: variants;
        /**
         * XNG-Changes
         */
        [fields.isMiniCart]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    const isCartLoading = cartState.isLoading;
    const { isUpdatingCart } = state.context;
    const {
        accountSettings: { allowGuestCheckout },
        cartSettings: { onePageCheckout },
        wishListSettings: { allowMultipleWishLists },
    } = getSettingsCollection(state);
    const { isSavingOrder } = state.pages.cart;

    return {
        cart,
        allowMultipleWishLists,
        isCartEmpty: isCartEmpty(cart),
        canSaveOrder: cartState.value?.canSaveOrder,
        canAddAllToList: canAddAllToList(cart),
        isPunchOutOrder: isPunchOutOrder(cart),
        canCheckoutWithCart: canCheckoutWithCart(cart),
        isCartCheckoutDisabled:
            isCartLoading ||
            !cart ||
            // xng-change - STR-2674 - Allow checkout for zero price bid products
            xIsCartCheckoutDisabled(cart) ||
            isUpdatingCart ||
            state.pages.signIn.isSigningInAsGuest,
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart),
        canSubmitForQuote: canSubmitForQuote(cart),
        isSubmitForQuoteButtonDisabled: isCartLoading || isUpdatingCart,
        rfqRequestQuotePageUrl: getPageLinkByPageType(state, "RfqRequestQuotePage")?.url,
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
        checkoutReviewAndSubmitPageUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        isSignedIn: cartState.value?.isAuthenticated && !cartState.value?.isGuestOrder,
        savedOrdersPageUrl: getPageLinkByPageType(state, "SavedOrderDetailsPage")?.url,
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        onePageCheckout,
        onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
        rememberMe: getSession(state).rememberMe,
        isSaveOrderDisabled:
            cartState.isLoading ||
            isSavingOrder ||
            isUpdatingCart ||
            cartState.value?.lineCount === 0 ||
            cartState.value?.cartLines?.length === 0,
        allowGuestCheckout,
        isApprovalEditDraft: cart?.status === "AwaitingApprovalEditDraft",
        isApprovalInProcess: cart?.orderApprovalStatus === "ApprovalInProcess",
        orderApprovalDetailsPageUrl: getPageLinkByPageType(state, "OrderApprovalDetailsPage")?.url,
    };
};

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    saveOrder,
    signInAsGuest,
    discardEdit,
    cancelApproval,
    // xng-change
    clearCurrentCart,
    addLinesToCart,
    trackBeginCheckout,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory & ResolveThunks<typeof mapDispatchToProps>;

export interface CartActionsStyles {
    narrowHidden?: HiddenProps;
    narrowOverflowMenu?: OverflowMenuPresentationProps;
    saveOrderClickable?: ClickablePresentationProps;
    addAllToListClickable?: ClickablePresentationProps;
    requestAQuoteClickable?: ClickablePresentationProps;
    checkoutClickable?: ClickablePresentationProps;
    wideHidden?: HiddenProps;
    wideOverflowMenu?: OverflowMenuPresentationProps;
    saveOrderButton?: ButtonPresentationProps;
    checkoutButton?: ButtonPresentationProps;
    discardEditWrapper?: InjectableCss;
    discardEditButton?: ButtonPresentationProps;
    cancelApprovalWrapper?: InjectableCss;
    cancelApprovalButton?: ButtonPresentationProps;
    // xng-change
    container?: InjectableCss;
    leftContainer?: InjectableCss;
    rightContainer?: InjectableCss;
    saveOrderLink?: LinkPresentationProps;
    printLink?: LinkPresentationProps;
    addAllProductGroupLink?: LinkPresentationProps;
    removeAllLink?: LinkPresentationProps;
}

export const cartActionsStyles: CartActionsStyles = {
    narrowHidden: {
        above: "sm",
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    wideHidden: {
        below: "md",
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    saveOrderButton: {
        buttonType: "outline",
        variant: "secondary",
    },
    wideOverflowMenu: {
        cssOverrides: {
            wrapper: css`
                margin-left: 20px;
            `,
        },
    },
    discardEditWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    discardEditButton: {
        variant: "secondary",
    },
    cancelApprovalWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    cancelApprovalButton: {
        variant: "secondary",
    },
};

const styles = cartActionsStyles;

const CartActions = ({
    cart,
    isCartEmpty,
    canAddAllToList,
    allowMultipleWishLists,
    setAddToListModalIsOpen,
    addToWishList,
    isPunchOutOrder,
    fields,
    canCheckoutWithCart,
    isCartCheckoutDisabled,
    hasOnlyQuoteRequiredProducts,
    canSubmitForQuote,
    isSubmitForQuoteButtonDisabled,
    history,
    rfqRequestQuotePageUrl,
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    hasQuoteRequiredProducts,
    checkoutReviewAndSubmitPageUrl,
    checkoutShippingPageUrl,
    isSignedIn,
    saveOrder,
    savedOrdersPageUrl,
    cartPageUrl,
    signInPageUrl,
    canSaveOrder,
    onePageCheckout,
    onePageCheckoutPageUrl,
    signInAsGuest,
    rememberMe,
    isSaveOrderDisabled,
    allowGuestCheckout,
    isApprovalEditDraft,
    orderApprovalDetailsPageUrl,
    discardEdit,
    isApprovalInProcess,
    cancelApproval,
    // xng-change
    clearCurrentCart,
    addLinesToCart,
    trackBeginCheckout,
}: Props) => {
    // xng-change
    if (!cart || isPunchOutOrder || (!isApprovalEditDraft && isCartEmpty)) {
        return null;
    }

    const toasterContext = useContext(ToasterContext);
    // const overflowItems = [];
    // const wideHiddenOverflowMenu = [];
    const [quoteRequiredModalIsOpen, setQuoteRequiredModalIsOpen] = useState(false);
    const [discardChangesModalIsOpen, setDiscardChangesModalIsOpen] = useState(false);

    // xng-change
    const [removeAllModalIsOpen, setRemoveAllModalIsOpen] = useState(false);
    // XNG-Changes: for saved cart name modal
    const [orderNameModalIsOpen, setOrderNameModalIsOpen] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);

    useEffect(() => {
        setAddToListModalIsOpen({ modalIsOpen: false });
    }, []);

    const addAllToListClickHandler = () => {
        const productInfos =
            cart.cartLines
                ?.filter(o => o.productId && o.qtyOrdered)
                .map(cartLine => ({
                    productId: cartLine.productId!,
                    qtyOrdered: cartLine.qtyOrdered!,
                    unitOfMeasure: cartLine.unitOfMeasure,
                })) || [];
        if (!allowMultipleWishLists) {
            addToWishList({
                productInfos,
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos });
    };

    const checkoutClickHandler = () => {
        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }

        if (!quoteRequiredModalIsOpen && hasQuoteRequiredProducts) {
            setQuoteRequiredModalIsOpen(true);
            return;
        }

        setQuoteRequiredModalIsOpen(false);

        const { isAuthenticated, isAwaitingApproval, id: cartId, canBypassCheckoutAddress } = cart;
        if (onePageCheckout && onePageCheckoutPageUrl) {
            if (!isAuthenticated && !rememberMe && allowGuestCheckout) {
                signInAsGuest({ returnUrl: onePageCheckoutPageUrl });
            } else {
                history.push(
                    isAwaitingApproval ? `${onePageCheckoutPageUrl}?cartId=${cartId}` : onePageCheckoutPageUrl,
                );
            }
            return;
        }

        if (canBypassCheckoutAddress) {
            // XNG-Changes
            trackBeginCheckout({});
            history.push(checkoutReviewAndSubmitPageUrl!);
            return;
        }

        preloadCheckoutShippingData({
            cartId: isAwaitingApproval ? cartId : undefined,
            onSuccess: () => {
                history.push(
                    isAwaitingApproval ? `${checkoutShippingPageUrl}?cartId=${cartId}` : checkoutShippingPageUrl!,
                );
            },
        });
    };

    const saveOrderClickHandler = () => {
        if (!isSignedIn) {
            setShowLoginModal(true);
            return;
        }

        setOrderNameModalIsOpen(true);
    };

    if (isApprovalEditDraft) {
        const discardEditClickHandler = async () => {
            discardEdit({
                cart,
                onSuccess: () => {
                    if (orderApprovalDetailsPageUrl) {
                        history.push(`${orderApprovalDetailsPageUrl}?cartId=${cart.orderNumber.split("_")[1]}`);
                    }
                },
            });
        };

        return (
            <StyledWrapper {...styles.discardEditWrapper}>
                <Button {...styles.discardEditButton} onClick={() => setDiscardChangesModalIsOpen(true)}>
                    {translate("Discard edit")}
                </Button>
                <TwoButtonModal
                    headlineText={translate("Are you sure?")}
                    modalIsOpen={discardChangesModalIsOpen}
                    messageText={siteMessage("OrderApproval_DiscardEditConfirmationMessage")}
                    cancelButtonText={translate("Continue editing")}
                    submitButtonText={translate("Discard edit")}
                    onCancel={() => setDiscardChangesModalIsOpen(false)}
                    onSubmit={discardEditClickHandler}
                ></TwoButtonModal>
            </StyledWrapper>
        );
    }

    if (isApprovalInProcess) {
        const cancelApprovalClickHandler = async () => {
            cancelApproval({
                cart,
                onSuccess: () => {
                    if (orderApprovalDetailsPageUrl) {
                        history.push(`${orderApprovalDetailsPageUrl}?cartId=${cart.id}`);
                    }
                },
            });
        };

        return (
            <StyledWrapper {...styles.cancelApprovalWrapper}>
                <Button {...styles.cancelApprovalButton} onClick={cancelApprovalClickHandler}>
                    {translate("Save and continue later")}
                </Button>
            </StyledWrapper>
        );
    }

    // <XNG-Changes>
    const saveOrderModalClickHandler = (savedOrderName: string, savedOrderId?: string) => {
        if (savedOrderId) {
            addLinesToCart({
                apiParameter: {
                    cartId: savedOrderId,
                    cartLineCollection: {
                        cartLines: cart?.cartLines?.map(o => o as any as CartLineModel),
                    } as CartLineCollectionModel,
                },
                onSuccess: () => {
                    history.push(`${savedOrdersPageUrl}?cartId=${savedOrderId}`);
                    // XNG-Changes: clear the cart after 2 seconds
                    // Had to add the timeout to prvent error modal throwing
                    setTimeout(() => {
                        clearCurrentCart();
                    }, 2000);
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        this.onSuccess?.();
                    } else {
                        toasterContext.addToast({ body: resultProps as any, messageType: "danger" });
                    }
                },
            });
        } else {
            saveOrder({
                ...{ savedOrderName },
                onSuccess: (orderId: string) => {
                    history.push(`${savedOrdersPageUrl}?cartId=${orderId}`);
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult?.cart) {
                        // "this" is targeting the object being created, not the parent SFC

                        this.onSuccess?.(resultProps.apiResult.cart.id);
                    }
                },
                onError: (errorMessage: string) => {
                    toasterContext.addToast({ body: errorMessage, messageType: "danger" });
                },
            });
        }
    };

    if (fields.isMiniCart) {
        const isDisabled = !canCheckoutWithCart || isCartCheckoutDisabled || hasOnlyQuoteRequiredProducts;
        return (
            <Button
                {...styles.checkoutButton}
                onClick={isDisabled ? undefined : checkoutClickHandler}
                disabled={isDisabled}
            >
                {cart?.requiresApproval
                    ? translate("Continue to Checkout for Approval")
                    : translate("Continue to Checkout")}
            </Button>
        );
    }
    // </XNG-Changes>

    // xng-change
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.leftContainer}>
                {canSaveOrder && (
                    <Link
                        {...styles.saveOrderLink}
                        onClick={saveOrderClickHandler}
                        disabled={isSaveOrderDisabled}
                        aria-label={translate("Save Cart")}
                        data-test-selector="cartActionsSaveCart"
                    >
                        {translate("Save Cart")}
                    </Link>
                )}
                <Link
                    {...styles.printLink}
                    onClick={openPrintDialog}
                    aria-label={translate("Print")}
                    data-test-selector="cartActionsPrint"
                >
                    {translate("Print")}
                </Link>
                {canAddAllToList && (
                    <Link
                        {...styles.addAllProductGroupLink}
                        onClick={addAllToListClickHandler}
                        aria-label={translate("Add All to Product Group")}
                        data-test-selector="cartActionsAddAllToProductGroup"
                    >
                        {translate("Add All to Product Group")}
                    </Link>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.rightContainer}>
                <Link
                    {...styles.removeAllLink}
                    onClick={() => setRemoveAllModalIsOpen(true)}
                    aria-label={translate("Remove All")}
                    data-test-selector="cartActionsRemoveAll"
                >
                    {translate("Remove All")}
                </Link>
            </StyledWrapper>
            <TwoButtonModal
                headlineText={translate("Remove All")}
                modalIsOpen={removeAllModalIsOpen}
                messageText={siteMessage("STR_Cart_RemoveAll")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Remove")}
                onCancel={() => setRemoveAllModalIsOpen(false)}
                onSubmit={() => {
                    setRemoveAllModalIsOpen(false);
                    clearCurrentCart();
                }}
                submitTestSelector="cart_RemoveAllSubmitButton"
                cancelTestSelector="cart_RemoveAllCancelButton"
                dataTestSelector="twoButtonModal_RemoveAllCartLines"
            />
            <SavedOrderNameModal
                modalIsOpen={orderNameModalIsOpen}
                onCancel={() => setOrderNameModalIsOpen(false)}
                onSubmit={(savedOrderName, savedOrderId) => saveOrderModalClickHandler(savedOrderName, savedOrderId)}
            />
            <Modal
                sizeVariant="small"
                headline={translate("Please sign in to Save Cart")}
                isOpen={showLoginModal}
                handleClose={() => setShowLoginModal(false)}
            >
                <StyledWrapper>
                    {createWidgetElement("SignIn/SignInExistingAccount", {
                        fields: {
                            isSigninModal: true,
                        },
                    })}
                </StyledWrapper>
            </Modal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CartActions)),
    definition: {
        group: "Cart",
        allowedContexts: ["CartPage"],
        fieldDefinitions: [
            {
                name: fields.isMiniCart,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: item => false,
            },
        ],
    },
};

export default widgetModule;
